import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import clsx from "clsx";

/** Tags as removable chips; Enter or a comma adds what's typed. Suggestions complete from tags already in use. */
export function TagInput({ value, onChange, suggest = [], invalid, label }: {
  value: string[]; onChange: (v: string[]) => void; suggest?: string[]; invalid?: boolean;
  /** What the input is called (its field's label), "Tags" when left out. */
  label?: string;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");
  const list = useId();
  const add = (raw: string) => {
    const tag = raw.replace(/,/g, " ").trim().replace(/\s+/g, " ");
    if (tag && !value.some((v) => v.toLowerCase() === tag.toLowerCase())) onChange([...value, tag]);
    setDraft("");
  };
  return (
    <div className={clsx("field flex min-h-[34px] flex-wrap items-center gap-1 py-1", invalid && "border-danger")}>
      {value.map((tag) => (
        <span key={tag} className="flex items-center gap-1 rounded-full bg-line-2 py-0.5 ps-2.5 pe-1.5 text-[13px]">
          {tag}
          <button type="button" className="text-ink-3 hover:text-danger" aria-label={`${t("common.remove")} ${tag}`} onClick={() => onChange(value.filter((v) => v !== tag))}>
            <X className="size-3.5" />
          </button>
        </span>
      ))}
      <input className="min-w-24 flex-1 bg-transparent outline-none" value={draft} list={suggest.length ? list : undefined} placeholder={value.length ? "" : t("common.tagsHint")}
        aria-label={label ?? t("common.tags")}
        onChange={(e) => (e.target.value.endsWith(",") ? add(e.target.value) : setDraft(e.target.value))}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); add(draft); }
          if (e.key === "Backspace" && !draft && value.length) onChange(value.slice(0, -1));
        }}
        onBlur={() => draft && add(draft)} />
      {suggest.length > 0 && <datalist id={list}>{suggest.filter((s) => !value.includes(s)).map((s) => <option key={s} value={s} />)}</datalist>}
    </div>
  );
}
